import { useState, useMemo } from 'react';
import { Copy, AlertCircle } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Textarea } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { copyToClipboard } from '@/lib/utils';
import { toast } from 'sonner';

const SAMPLE =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IlV0aWxpdHkgT1MiLCJpYXQiOjE3MDAwMDAwMDAsImV4cCI6OTk5OTk5OTk5OX0.S4gPkQ4-h9-fgN-UoqpY3Kxo2qHlCJ4KgGHqMrPm8Q4';

function decodeBase64Url(s: string): string {
  const pad = '==='.slice((s.length + 3) % 4);
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + pad;
  return decodeURIComponent(escape(atob(b64)));
}

interface ParsedJwt {
  header: unknown;
  payload: unknown;
  signature: string;
  raw: { header: string; payload: string };
}

export function JwtDecoder() {
  const [token, setToken] = useState(SAMPLE);

  const parsed = useMemo<{ ok: true; data: ParsedJwt } | { ok: false; error: string }>(() => {
    if (!token.trim()) return { ok: false, error: 'No token' };
    const parts = token.trim().split('.');
    if (parts.length !== 3) return { ok: false, error: 'Token should have 3 parts' };
    try {
      const headerStr = decodeBase64Url(parts[0]);
      const payloadStr = decodeBase64Url(parts[1]);
      return {
        ok: true,
        data: {
          header: JSON.parse(headerStr),
          payload: JSON.parse(payloadStr),
          signature: parts[2],
          raw: { header: headerStr, payload: payloadStr },
        },
      };
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : 'Invalid token' };
    }
  }, [token]);

  const expiry = useMemo(() => {
    if (!parsed.ok) return null;
    const p = parsed.data.payload as Record<string, unknown>;
    if (typeof p.exp !== 'number') return null;
    const ms = p.exp * 1000;
    const expired = Date.now() > ms;
    return { expired, when: new Date(ms).toLocaleString() };
  }, [parsed]);

  return (
    <div>
      <ToolHeader
        toolId="jwt-decode"
        title="JWT decoder"
        description="Inspect a JSON Web Token's header, payload, and signature. (Decodes only — does not verify.)"
        badges={
          parsed.ok && expiry ? (
            <Badge variant={expiry.expired ? 'danger' : 'success'}>
              {expiry.expired ? 'expired' : 'active'}
            </Badge>
          ) : null
        }
      />

      <Card className="mb-4 !p-0">
        <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
          <span className="text-xs font-medium text-fg-muted">Token</span>
          <span className="text-2xs text-fg-subtle">{token.length} chars</span>
        </div>
        <Textarea
          mono
          value={token}
          onChange={(e) => setToken(e.target.value)}
          className="!border-0 !rounded-t-none min-h-[120px] !bg-transparent focus-visible:!ring-0 break-all"
          placeholder="Paste a JWT…"
        />
      </Card>

      {!parsed.ok ? (
        <Card>
          <div className="flex items-center gap-2 text-danger">
            <AlertCircle size={14} />
            <div className="text-sm font-medium">{parsed.error}</div>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {expiry && (
            <Card>
              <div className="text-xs font-medium text-fg-muted mb-1">Expiry</div>
              <div className="flex items-center gap-2 text-sm">
                <Badge variant={expiry.expired ? 'danger' : 'success'}>
                  {expiry.expired ? 'expired' : 'active'}
                </Badge>
                <span className="font-mono text-fg-muted">{expiry.when}</span>
              </div>
            </Card>
          )}

          <SectionCard title="Header" data={parsed.data.header} />
          <SectionCard title="Payload" data={parsed.data.payload} />

          <Card className="!p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-2xs uppercase tracking-wider text-fg-subtle mb-1">
                  Signature
                </div>
                <div className="font-mono text-sm break-all">{parsed.data.signature}</div>
              </div>
              <button
                onClick={async () => {
                  const ok = await copyToClipboard(parsed.data.signature);
                  if (ok) toast.success('Signature copied');
                }}
                className="shrink-0 size-8 rounded-md hover:bg-surface-hover flex items-center justify-center text-fg-subtle hover:text-fg"
              >
                <Copy size={12} />
              </button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

function SectionCard({ title, data }: { title: string; data: unknown }) {
  const json = JSON.stringify(data, null, 2);
  return (
    <Card className="!p-0">
      <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
        <span className="text-xs font-medium text-fg-muted">{title}</span>
        <button
          onClick={async () => {
            const ok = await copyToClipboard(json);
            if (ok) toast.success(`${title} copied`);
          }}
          className="text-fg-subtle hover:text-fg"
        >
          <Copy size={12} />
        </button>
      </div>
      <pre className="font-mono text-[13px] leading-relaxed p-4 overflow-auto max-h-72">
        <code>{json}</code>
      </pre>
    </Card>
  );
}

export default JwtDecoder;
